import { orgAPI } from "@/api/api";
import type { IntegrationConnection } from "@cohesive/api-client";
import { MicrosoftTeamsUserProfileImageEntity } from "@cohesive/api-entities";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";

interface UseMicrosoftTeamsProfileImageParams {
  connection: IntegrationConnection;
  userID: string;
  size?: string; // e.g., '48x48', '120x120', etc.
}

export function useMicrosoftTeamsProfileImage(
  params: UseMicrosoftTeamsProfileImageParams,
  options?: UseQueryOptions<string, AxiosError>,
) {
  return useQuery<string, AxiosError>({
    queryKey: [
      "microsoft-teams-profile-image",
      params.connection.id,
      params.userID,
      params.size,
    ],
    retry: false,
    queryFn: async () => {
      const response = await orgAPI.toolbox.execute<
        MicrosoftTeamsUserProfileImageEntity,
        Record<string, unknown>
      >(
        {
          integration_connection_id: params.connection.id,
          action_name: "microsoft-teams-get-user-profile-image",
          action_input: {
            user_id: params.userID,
            size: params.size,
          },
        },
        {
          validateStatus: (status) => {
            if (status === 404) {
              return true;
            }
            return status >= 200 && status < 300;
          },
        },
      );

      try {
        return `data:${response.data.data.content_type};base64,${response.data.data.data}`;
      } catch (e) {
        return "";
      }
    },
    enabled: !!params.connection.integration_id && !!params.userID,
    staleTime: 1000 * 60,
    ...options,
  });
}
